<template>
  <v-container class="pa-0" fluid>
    <v-card>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row class="pt-3 pb-0">
            <v-col cols="8" class="py-0 pl-3">
              <span class="table-card-title pl-0">Créditos de Cashback bloqueados</span> <br />
              <p class="subtitle pt-1" style="word-break: break-word">
                Quando um consumidor com cashback ativado se identifica no PDV, sua carteira de cashback é bloqueada durante a transação para impedir que
                movimentações de saldo aconteçam durante a compra. No entanto, devido a eventuais falhas de comunicação entre o PDV e a Mercafacil, a carteira
                pode continuar bloqueada mesmo após a compra ter sido confirmada ou cancelada no PDV. Aqui você pode remover o bloqueio de carteiras de cashback
                de um consumidor da sua rede. Abaixo você confere os bloqueios que você tem.
              </p>
            </v-col>
            <v-col cols="4" class="py-0">
              <v-row class="pr-3">
                <v-col class="search-and-export-header" cols="12">
                  <div>
                    <mf-search
                      v-model="search"
                      placeholder="Busque por CPF/CNPJ"
                      hide-details
                      :disabled="!hasPiiAccess"
                      :tooltipMessage="!hasPiiAccess ? 'Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso' : ''"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
        <paginated-data-table
          ref="table"
          :headers="headers"
          :search="search"
          :query="query"
          :custom-items-per-page="10"
          :queryName="'absoluteDiscountLocks'"
          :queryMainPropertyName="'absolute_discount_locks'"
          :queryVariables="queryVariables"
          default-table-sort-by="locked_at"
          :default-table-sort-desc="true"
          :graphql-uri="$microservicesUrls['cashback']"
          :loading="loading"
          @update:maxRecords="updateMaxRecords"
        >
          <template v-slot:item.user.document_no="{ item }">
            <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <template v-if="hasPiiAccess"> {{ getSafe(() => item.user.document_no) | cpfCnpj }}</template>
                  <template v-else>
                    {{ item.user.document_no }}
                  </template>
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </template>
          <template v-slot:item.unit.name="{ item }">
            {{ getSafe(() => item.unit.name) | hifen }}
          </template>
          <template v-slot:item.locked_by="{ item }">
            {{ item.locked_by }}
          </template>
          <template v-slot:item.locked_at="{ item }">
            {{ DateTime.fromISO(item.locked_at).toFormat('dd/MM/yyyy HH:mm:ss') }}
          </template>
          <template v-slot:item.absolute_discount_value="{ item }">
            {{ item.absolute_discount_value ? 'R$' : '' }}{{ item.absolute_discount_value | localeString(2) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-menu>
              <template v-slot:activator="{ on }">
                <mf-button color="cinza-0" icon="more_vert" type="icon" v-on:click.native="on.click" />
              </template>
              <v-list>
                <v-list-item @click="releaseAbsoluteDiscountLock({ documentNo: item.user.document_no, absoluteDiscountLockId: item._id })">
                  <v-list-item-title>Desbloquear cashback</v-list-item-title>
                </v-list-item>
                <v-list-item @click="releaseAndConsumeAbsoluteDiscountLock({ documentNo: item.user.document_no, absoluteDiscountLockId: item._id })">
                  <v-list-item-title>Desbloquear cashback e consumir saldo</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </paginated-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
// TODO: Retrigger load after adjustment.

import PaginatedDataTable from '@/components/PaginatedDataTable'
import {
  QUERY_GET_ABSOLUTE_DISCOUNT_LOCKS,
  MUTATION_RELEASE_ABSOLUTE_DISCOUNT_LOCK,
  MUTATION_RELEASE_ABSOLUTE_DISCOUNT_AND_CONSUME_LOCK,
} from '@/modules/industria/cashback/graphql'
import { DateTime } from 'luxon'
import cpfCnpj from '@/filters/cpfCnpj.js'

export default {
  name: 'NextbuyCashbackBalanceLocksTable',
  components: {
    PaginatedDataTable,
  },
  data: () => ({
    search: '',
    loading: false,
    maxRecords: 0,
  }),
  computed: {
    DateTime() {
      return DateTime
    },
    query() {
      return QUERY_GET_ABSOLUTE_DISCOUNT_LOCKS
    },
    queryVariables() {
      return {
        search: this.search,
        type: 'nextbuycashback',
      }
    },
    headers() {
      return [
        {
          text: 'CPF/CNPJ',
          value: 'user.document_no',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Loja',
          value: 'unit.name',
          width: '20%',
          sortable: false,
        },
        {
          text: 'ID da transação',
          value: 'locked_by',
          width: '20%',
        },
        {
          text: 'Data do bloqueio',
          value: 'locked_at',
          width: '15%',
        },
        {
          text: 'Saldo',
          value: 'absolute_discount_value',
          width: '10%',
        },
        {
          text: '',
          value: 'actions',
          width: '5%',
          sortable: false,
        },
      ]
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  methods: {
    getLockedByLabel(lockedBy) {
      if (!lockedBy) return '-'
      const lockedByLabels = {
        jane: 'PDV',
        converter: 'Ativação',
      }
      return lockedByLabels[lockedBy] || '-'
    },
    reload() {
      this.$refs.table.getData()
    },
    updateMaxRecords(records) {
      this.maxRecords = records
    },
    async releaseAbsoluteDiscountLock({ documentNo, absoluteDiscountLockId }) {
      const confirmed = await this.$confirm({
        title: 'Desbloquear carteira sem consumir saldo',
        message: `Tem certeza que deseja desbloquear o consumo de cashback do cliente com o CPF ${cpfCnpj(
          documentNo
        )} sem consumir seu saldo? Ao confirmar essa ação, o saldo deste cliente será redisponibilizado.`,
        confirmText: 'Desbloquear',
        confirmColor: 'primary',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) {
        return
      }
      this.loading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_RELEASE_ABSOLUTE_DISCOUNT_LOCK,
          variables: {
            _id: absoluteDiscountLockId,
          },
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        this.reload()
      } catch (e) {
        this.$snackbar({ message: 'Erro ao desbloquear cashback', snackbarColor: 'error' })
      }
      this.loading = false
    },
    async releaseAndConsumeAbsoluteDiscountLock({ documentNo, absoluteDiscountLockId }) {
      const confirmed = await this.$confirm({
        title: 'Desbloquear carteira consumindo o saldo',
        message: `Tem certeza que deseja desbloquear o consumo de cashback do CPF ${cpfCnpj(
          documentNo
        )} consumindo seu saldo? Ao confirmar essa ação, o saldo de cashback ativo será debitado da carteira deste cliente.`,
        confirmText: 'Desbloquear e consumir saldo',
        confirmColor: 'primary',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) {
        return
      }
      this.loading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_RELEASE_ABSOLUTE_DISCOUNT_AND_CONSUME_LOCK,
          variables: {
            _id: absoluteDiscountLockId,
          },
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        this.reload()
      } catch (e) {
        this.$snackbar({ message: 'Erro ao desbloquear e consumir cashback', snackbarColor: 'error' })
      }
      this.loading = false
    },
  },
}
</script>
<style lang="scss" scoped>
.search-and-export-header {
  display: flex !important;
  justify-content: flex-end;
}

.search-and-export-header > span:first-child {
  align-self: center;
  margin: 0 8px 0 8px;
}

.search-and-export-header > div {
  width: 100%;
  max-width: 300px;
  min-width: 110px;
}
</style>
